import { useEffect, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { downloadCsv } from "@/lib/csvExport";
import { fmtQty, type AgeingData, type MoversData } from "@/types/inventory";

const th = "px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold text-left whitespace-nowrap";
const KIND: Record<string, { label: string; cls: string }> = {
  DEAD: { label: "Not moving", cls: "bg-rose-500/10 text-rose-700 border-rose-300" },
  SLOW: { label: "Slow", cls: "bg-amber-500/10 text-amber-700 border-amber-300" },
  FAST: { label: "Moving", cls: "bg-emerald-500/10 text-emerald-700 border-emerald-300" },
};

/** Stock ageing and fast / slow / dead stock, each downloadable as CSV */
export function InventoryReportsPage() {
  const { error } = useToast();
  const [tab, setTab] = useState<"ageing" | "movers">("ageing");
  const [days, setDays] = useState(90);
  const [ageing, setAgeing] = useState<AgeingData | null>(null);
  const [movers, setMovers] = useState<MoversData | null>(null);

  useEffect(() => {
    api.get("/api/StockReport/Ageing").then((r) => setAgeing(unwrapObject<AgeingData>(r))).catch((e: any) => error("Fetch Error", e.message || "Failed to load"));
  }, []);
  useEffect(() => {
    setMovers(null);
    api.get(`/api/StockReport/Movers?days=${days}`).then((r) => setMovers(unwrapObject<MoversData>(r))).catch((e: any) => error("Fetch Error", e.message || "Failed to load"));
  }, [days]);

  const exportAgeing = () => ageing && downloadCsv("stock-ageing.csv", ["Product", "Shade", "Unit", "Rolls", "Total", ...ageing.buckets.map((b) => b.label), "Oldest (days)"], ageing.rows.map((r) => [r.product_name, r.variant_name, r.unit, r.rolls, r.total_qty, ...ageing.buckets.map((b) => r[b.key]), r.oldest_days]));
  const exportMovers = () => movers && downloadCsv("stock-movers.csv", ["Product", "Code", "Unit", "On hand", `Sold (${movers.days} days)`, "Per day", "Days of cover", "Last sold", "Status"], movers.rows.map((r) => [r.product_name, r.product_code, r.unit, r.on_hand, r.sold, r.per_day, r.days_cover, r.last_sold_on?.slice(0, 10), KIND[r.kind]!.label]));

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Stock reports</h1>
          <p className="text-sm text-muted-foreground mt-0.5">How old your stock is and what is not selling</p>
        </div>
        <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={tab === "ageing" ? exportAgeing : exportMovers}>
          <Download className="size-4" /> Download CSV
        </Button>
      </div>
      <div className="flex gap-1 rounded-lg bg-muted p-1 w-fit">
        {([["ageing", "Stock ageing"], ["movers", "Fast and slow"]] as const).map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={`h-8 px-4 rounded-md text-sm font-medium ${tab === k ? "bg-background shadow-xs text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "ageing" &&
        (!ageing ? <Loader2 className="size-5 animate-spin text-muted-foreground" /> : (
          <div className="bg-card border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr><th className={th}>Product</th><th className={th}>Rolls</th><th className={th}>Total</th>{ageing.buckets.map((b) => <th key={b.key} className={th}>{b.label}</th>)}</tr></thead>
              <tbody className="divide-y">
                {ageing.rows.map((r) => (
                  <tr key={`${r.product_id}${r.variant_name}${r.unit}`}>
                    <td className="px-3 py-2"><div className="font-medium">{r.product_name}</div><div className="text-[11px] text-muted-foreground">{r.variant_name || "No shade"} · oldest {r.oldest_days} days</div></td>
                    <td className="px-3 py-2">{r.rolls}</td>
                    <td className="px-3 py-2 font-semibold">{fmtQty(r.total_qty, r.unit)}</td>
                    {ageing.buckets.map((b, i) => <td key={b.key} className={`px-3 py-2 ${r[b.key] > 0 && i >= 3 ? "text-rose-600 font-semibold" : r[b.key] > 0 && i === 2 ? "text-amber-600" : "text-muted-foreground"}`}>{r[b.key] > 0 ? fmtQty(r[b.key]) : "-"}</td>)}
                  </tr>
                ))}
                {!ageing.rows.length && <tr><td className="px-3 py-6 text-muted-foreground" colSpan={8}>No stock on hand</td></tr>}
              </tbody>
              {Object.keys(ageing.totals).length > 0 && (
                <tfoot className="border-t bg-muted/30">
                  {Object.entries(ageing.totals).map(([unit, t]) => (
                    <tr key={unit} className="font-semibold"><td className="px-3 py-2">Total ({unit})</td><td /><td className="px-3 py-2">{fmtQty(t.total_qty, unit)}</td>{ageing.buckets.map((b) => <td key={b.key} className="px-3 py-2">{fmtQty(t[b.key])}</td>)}</tr>
                  ))}
                </tfoot>
              )}
            </table>
          </div>
        ))}

      {tab === "movers" && (
        <>
          <div className="flex items-center gap-2 text-sm">
            Looking at the last
            <select className="h-9 rounded-lg border border-input bg-background px-3 text-sm" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[30, 60, 90, 180, 365].map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
          </div>
          {!movers ? <Loader2 className="size-5 animate-spin text-muted-foreground" /> : (
            <div className="bg-card border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr><th className={th}>Product</th><th className={th}>On hand</th><th className={th}>Sold ({movers.days} days)</th><th className={th}>Per day</th><th className={th}>Days of cover</th><th className={th}>Last sold</th><th className={th}>Status</th></tr></thead>
                <tbody className="divide-y">
                  {movers.rows.map((r) => (
                    <tr key={r.product_id}>
                      <td className="px-3 py-2"><div className="font-medium">{r.product_name}</div><div className="text-[11px] text-muted-foreground">{r.product_code}</div></td>
                      <td className="px-3 py-2">{fmtQty(r.on_hand, r.unit)}</td>
                      <td className="px-3 py-2">{fmtQty(r.sold, r.unit)}</td>
                      <td className="px-3 py-2">{r.per_day}</td>
                      <td className="px-3 py-2">{r.days_cover ?? "-"}</td>
                      <td className="px-3 py-2 text-xs">{r.last_sold_on ? new Date(r.last_sold_on).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "Never"}</td>
                      <td className="px-3 py-2"><span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${KIND[r.kind]!.cls}`}>{KIND[r.kind]!.label}</span></td>
                    </tr>
                  ))}
                  {!movers.rows.length && <tr><td className="px-3 py-6 text-muted-foreground" colSpan={7}>Nothing in stock and nothing sold</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
